import { get, isUndefined, zip } from 'lodash-es';
import dynamic from 'next/dynamic';
import React, { useMemo } from 'react';

const Anchor = dynamic(() => import('./Anchor'));

// Available Component to change
enum ComponentEnum {
  Phone = 'phone',
  Email = 'email',
  CemPhone = 'cemPhone',
  CemEmail = 'cemEmail',
  Link = 'link',
  Strong = 'strong',
}

export type Text = { text: string };
type TargetComponentType = {
  customComponents?: Partial<Record<ComponentEnum | string, React.FC<Text>>>;
};

type RichTextParserProps = Text & TargetComponentType;

// Generic email, link, phone number regexes
const EMAIL_REGEX = /(\b\S+@[\w.+-]+\.\w{2,4}\b)/g;
const STRONG_REGEX = /<strong>([\s\S]*?)<\/strong>/g;
const PHONE_REGEX = /\+?(\d{2})?([- ])?(\(\d\))?(\d{3,5})(([- ])?\d{1,4}){2,4}/g;
const CEM_EMAIL_REGEX = new RegExp(`📧: ${EMAIL_REGEX.source}`, 'g');
const CEM_PHONE_REGEX = new RegExp(`☎️: ${PHONE_REGEX.source}`, 'g');
const LINK_REGEX = /http[s]?:\/\/(\b(\w+:\/\/)?[^@\s]{2,}\.[a-z]{2,}\b)/g;

// Objects to ease the mapping
const DEFAULT_COMPONENTS = {
  [ComponentEnum.Email]: ({ text }: Text) => <Anchor text={text} prefix='mailto:' />,
  [ComponentEnum.Link]: ({ text }: Text) => <Anchor text={text} />,
  [ComponentEnum.Phone]: ({ text }: Text) => <Anchor text={text} prefix='tel:' />,
  [ComponentEnum.Strong]: ({ text }: Text) => <span className='font-bold'>{text.replace(/<\/?strong>/g, '')}</span>,
};

// Keeping this order to prevent smaller groups from matching bigger one
// (e.g link regex could take the domain chunk from an email)
const ORDERED_REGEX_AND_TYPE = [
  { regex: CEM_EMAIL_REGEX, type: ComponentEnum.CemEmail },
  { regex: EMAIL_REGEX, type: ComponentEnum.Email },
  { regex: CEM_PHONE_REGEX, type: ComponentEnum.CemPhone },
  { regex: LINK_REGEX, type: ComponentEnum.Link },
  { regex: PHONE_REGEX, type: ComponentEnum.Phone },
  { regex: STRONG_REGEX, type: ComponentEnum.Strong },
];

// a generic regex to catch all available expressions
const RICH_TEXT_PART_REGEX = new RegExp(ORDERED_REGEX_AND_TYPE.map(rg => rg.regex.source).join('|'), 'gi');

/**
 * Generates a list of chunks matching the generic regex
 * @param {String} text to be parsed
 * @returns {String[]} list of chunks matching the generic regex
 */
const parseTextIntoParts = (text: string) => {
  const matchedItems = text.match(RICH_TEXT_PART_REGEX);
  if (!matchedItems) return [text];

  // contiously split the text into parts through each matched regex
  let parts = [text];
  matchedItems.forEach((parsedItem: string) => {
    parts = parts.map(part => part.split(parsedItem)).flat();
  });

  const partsBeforeMatches = text.indexOf(parts[0]) < text.indexOf(matchedItems[0]);

  // re-concatenating the parts and matched items depending on which part is present first in the original text
  return zip(partsBeforeMatches ? parts : matchedItems, partsBeforeMatches ? matchedItems : parts)
    .flat()
    .filter(orderedPart => !isUndefined(orderedPart));
};

/**
 * Reconstruct original text and parses items that matches generic regexes (Phone, Email, Link ..etc)
 * into provided react components
 */
const RichTextParser: React.FC<RichTextParserProps> = ({ text, customComponents = [] }) => {
  const components = useMemo(() => ({ ...DEFAULT_COMPONENTS, ...customComponents }), [customComponents]);
  const parseContentToComponents = useMemo(() => {
    const parsedText = parseTextIntoParts(text);
    const parsedComponents = parsedText.map((part, idx) => {
      if (!part) return null;
      for (const regexAndType of ORDERED_REGEX_AND_TYPE) {
        if (regexAndType.regex.test(part)) {
          const ChosenComponent = get(components, regexAndType.type) ?? ((props: Text) => props.text);
          // eslint-disable-next-line react/no-array-index-key
          return <ChosenComponent key={idx} text={part} />;
        }
      }
      // eslint-disable-next-line react/no-array-index-key
      return <React.Fragment key={idx}>{part}</React.Fragment>;
    });
    return parsedComponents;
  }, [text, components]);

  return parseContentToComponents;
};

export default RichTextParser;
